// AI-CONTEXT: See docs/features/impact-per-euro-methodiek.md
import { jaargemiddeldTarief } from './schaduwtarief'

/**
 * @typedef {object} NoemerInput
 * @property {number|string} totalCosts - Kosten conform de jaarrekening (verplicht, > 0)
 * @property {number|string} [supportVolunteerHours] - Uren onbetaalde ondersteunende inzet in het boekjaar (géén deelnemers — § 2.3)
 * @property {number|string} [incidentalCosts] - Incidentele posten die gecorrigeerd worden (§ 2.4, optioneel)
 * @property {number|string} [totalIncome] - Totale inkomsten van het boekjaar (voor de noemer-ondergrens, § 4)
 * @property {number} year - Boekjaar (= kalenderjaar, D3) — bepaalt het schaduwtarief
 */

/**
 * @typedef {object} NoemerResultaat
 * @property {number} noemer - De totale inzet waarmee de gemeten waarde wordt gedeeld
 * @property {{ kosten: number, vrijwilligersWaarde: number, incidenteleCorrectie: number, uurtarief: number|null }} componenten - Opbouw voor de presentatie
 * @property {boolean} ondergrensToegepast - true als inkomsten > inzet en de noemer is opgehoogd (§ 4)
 * @property {boolean} incidenteleOnderDrempel - true als er gecorrigeerd is terwijl de post ≤ 10% van de kosten is (methodiek staat correctie alleen dáárboven toe — zichtbaar vermelden)
 */

/**
 * De noemer van het kengetal (methodiek § 2 + § 4): jaarrekeningkosten, minus
 * een eventuele incidentele correctie, plus de waarde van onbetaalde
 * ondersteunende inzet tegen het jaargemiddelde schaduwtarief — met als
 * ondergrens de totale inkomsten van het boekjaar.
 *
 * Geen stille reparaties: ongeldige of niet-positieve kosten, en een
 * incidentele correctie die de kosten volledig opslokt, geven null zodat de
 * kaart een eerlijke lege staat toont.
 *
 * @param {NoemerInput} input
 * @returns {NoemerResultaat|null}
 */
const berekenNoemer = ({ totalCosts, supportVolunteerHours, incidentalCosts, totalIncome, year } = {}) => {
  const kosten = Number(totalCosts)
  if (!Number.isFinite(kosten) || kosten <= 0) return null

  const incidenteel = Number(incidentalCosts)
  const incidenteleCorrectie = Number.isFinite(incidenteel) && incidenteel > 0 ? incidenteel : 0
  if (incidenteleCorrectie >= kosten) return null

  const uren = Number(supportVolunteerHours)
  const uurtarief = jaargemiddeldTarief(year)
  const vrijwilligersWaarde = Number.isFinite(uren) && uren > 0 && uurtarief !== null
    ? uren * uurtarief
    : 0

  const inzet = kosten - incidenteleCorrectie + vrijwilligersWaarde

  const inkomsten = Number(totalIncome)
  const ondergrensToegepast = Number.isFinite(inkomsten) && inkomsten > 0 && inkomsten > inzet

  return {
    noemer: ondergrensToegepast ? inkomsten : inzet,
    componenten: {
      kosten,
      vrijwilligersWaarde,
      incidenteleCorrectie,
      uurtarief,
    },
    ondergrensToegepast,
    incidenteleOnderDrempel: incidenteleCorrectie > 0 && incidenteleCorrectie <= 0.1 * kosten,
  }
}

export default berekenNoemer
